import GlobalStyles from '@mui/system/GlobalStyles'
import * as turf from '@turf/turf'
import { Account, MapboxMapController } from '@xweather/mapsgl'
import { LngLat, LngLatBounds } from 'mapbox-gl'
import { enqueueSnackbar } from 'notistack'
import { type ComponentProps, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AttributionControl, type AttributionControlProps, Map as MapGL } from 'react-map-gl/mapbox'
import { authManager } from '../../auth-manager.ts'
import { useInsightTokens } from '../../data/insight.ts'
import { useLocations } from '../../data/locations.tsx'
import { useGetProtectionsByAsset } from '../../data/protections/useProtections.ts'
import { usePreferences } from '../../data/userProfile.ts'
import { env } from '../../env.ts'
import { urlHash } from '../../router.tsx'
import { useMap, useMapState, useMapsgl, useMyLocation } from '../../states.ts'
import { createBufferedRing } from '../alerts/utils.tsx'
import { createZIndexLayers, dragPanOptions, fitBounds, flyTo, mapStyles, updateHash } from './mapUtils.ts'

export const MapBox = ({
	selectedLocationId,
	selectedCoords,
	attributionProps,
	disableCameraController,
	...props
}: {
	selectedLocationId: string | undefined
	selectedCoords?: { lat: number; lon: number }
	attributionProps?: AttributionControlProps
	disableCameraController?: boolean
} & Omit<ComponentProps<typeof MapGL>, 'mapStyle'>) => {
	const { data: tokens } = useInsightTokens()
	const [ready, setReady] = useState(false)
	const mapStyle = useMapState((s) => s.mapStyle)
	const map = useMap((s) => s.map)
	const setMap = useMap((s) => s.setMap)
	const setMapController = useMapsgl((s) => s.setMapController)
	const ctrl = useMapsgl((s) => s.controller)
	const { data: preferences } = usePreferences()
	const language = Intl.getCanonicalLocales(preferences.language)[0]

	useEffect(() => {
		if (!map) return

		// When map style changes (ie light, dark, satellite), we need to re-create the z-index layers
		const recreateZIndexLayers = () => createZIndexLayers(map)
		map.on('style.load', recreateZIndexLayers)
		return () => {
			map.off('style.load', recreateZIndexLayers)
		}
	}, [map])

	useEffect(() => {
		if (!map || !props.logoPosition) return
		// A reused map keeps the logo where it was created, so move it
		const logo = map.getContainer().querySelector('.mapboxgl-ctrl-logo')?.parentElement
		const target = map.getContainer().querySelector(`.mapboxgl-ctrl-${props.logoPosition}`)
		const origin = logo?.parentElement
		if (!logo || !target || !origin || origin === target) return
		target.prepend(logo)
		return () => origin.append(logo)
	}, [map, props.logoPosition])

	useEffect(() => {
		if (!map || !ready) return
		if (map.getLanguage() === language) return
		map.setLanguage(language)
	}, [language, map, ready])

	return (
		<MapGL
			reuseMaps
			id='map'
			mapboxAccessToken={tokens?.MAPBOX}
			mapStyle={mapStyles[mapStyle]}
			attributionControl={false}
			pitchWithRotate={false}
			dragRotate={false}
			dragPan={dragPanOptions}
			touchPitch={false}
			maxZoom={18}
			minZoom={3}
			keyboard={false}
			language={language}
			{...props}
			fadeDuration={0}
			ref={(ref) => ref?.getMap() && setMap(ref.getMap())}
			onLoad={({ target: map }) => {
				map.touchZoomRotate.disableRotation()
				createZIndexLayers(map)
				if (!ready) setReady(true)
				if (!ctrl) {
					const controller = new MapboxMapController(map, {
						account: new Account(env.XWEATHER_ID, tokens?.XWEATHER ?? ''),
						animation: { preloadData: true },
					})
					controller.on('load', () => setMapController(controller))
				}
			}}
			onRemove={() => {
				setMap(undefined)
			}}
			onMoveEnd={(e) => {
				const { latitude = 0, longitude = 0, zoom = 5 } = e.viewState
				const state = { lat: +latitude.toFixed(5), lon: +longitude.toFixed(5), z: +zoom.toFixed(3) }
				updateHash(e.target, state)
			}}
			transformRequest={(url) => {
				if (url.includes('geop')) {
					const accessToken = authManager.getAccessTokenSync()
					if (accessToken) {
						url = url.includes('?') ? `${url}&accessToken=${accessToken}` : `${url}?accessToken=${accessToken}`
					}
					return { url }
				}
				return { url }
			}}
		>
			<GlobalStyles
				styles={{
					'.mapboxgl-map': { font: 'inherit' },
					'.mapboxgl-ctrl-bottom-left': { display: 'flex', alignItems: 'end', gap: 8, padding: '0 0 4px 4px' },
					'.mapboxgl-ctrl, .mapboxgl-ctrl-logo': { margin: '0 !important' },
					'.mapboxgl-ctrl-attrib': { margin: '0 !important', order: 1 },
					'.mapboxgl-ctrl': { display: 'block !important' }, // Hack to fix https://github.com/mapbox/mapbox-gl-js/issues/13561
				}}
			/>
			{!disableCameraController && <MapCameraController selectedLocationId={selectedLocationId} selectedCoords={selectedCoords} />}
			<AttributionControl position='bottom-left' {...attributionProps} />
			{props.children}
		</MapGL>
	)
}

const MapCameraController = ({
	selectedLocationId,
	selectedCoords,
}: {
	selectedLocationId: string | undefined
	selectedCoords?: { lat: number; lon: number }
}) => {
	const { t } = useTranslation()
	const map = useMap((s) => s.map)
	const { data: locations, isPending: fetchingLocations } = useLocations()
	const isFollowing = useMyLocation((s) => s.isFollowing)
	const setIsFollowing = useMyLocation((s) => s.setIsFollowing)
	const myCoords = useMyLocation((s) => s.coords)
	const setMyCoords = useMyLocation((s) => s.setCoords)
	const firstRender = useRef(true)
	const location = locations?.find((a) => a.id === selectedLocationId)
	const protections = useGetProtectionsByAsset(location?.type === 'asset' ? location.assetVrn : '')
	// Radius of largest lightning protection, if any
	const bufferRadius = protections.reduce(
		(acc, cur) => (cur.type === 'lightning' && cur.radius > acc.radius ? { radius: cur.radius } : acc),
		{ radius: 0 },
	)

	useEffect(() => {
		if (map && isFollowing && myCoords) {
			const animate = !firstRender.current
			flyTo(map, [myCoords.longitude, myCoords.latitude], { animate })
			firstRender.current = false
		}
	}, [map, isFollowing, myCoords])

	useEffect(() => {
		if (map && isFollowing) {
			navigator.geolocation.getCurrentPosition(
				({ coords }) => setMyCoords(coords),
				() => {
					enqueueSnackbar(
						t(($) => $.snackbar.myLocationError),
						{ variant: 'error' },
					)
					setIsFollowing(false)
				},
			)
			const watchId = navigator.geolocation.watchPosition(({ coords }) => setMyCoords(coords))
			return () => navigator.geolocation.clearWatch(watchId)
		}
	}, [map, isFollowing, setMyCoords, t, setIsFollowing])

	useEffect(() => {
		if (!map) return
		const animate = !firstRender.current

		if (location) {
			if (location.type === 'asset' && location.geometry) {
				fitBounds(
					map,
					// We do not want to use `createBufferRing with a radius of 0 as the underlying turf.buffer call returns undefined with a radius of 0
					turf.bbox(bufferRadius.radius > 0 ? createBufferedRing(location.geometry, bufferRadius.radius, 'kilometres') : location.geometry),
					{ padding: getPadding(true), animate, maxZoom: 10 },
				)
			} else {
				flyTo(map, location, { animate, padding: { bottom: 150 } })
				if (map?.getBounds()?.contains(location)) updateHash(map, { lat: map.getCenter().lat, lon: map.getCenter().lng, z: map.getZoom() })
			}
			firstRender.current = false
			setIsFollowing(false)
			return
		}

		const coords = urlHash.get()
		if (coords.lat != null && coords.lon != null) {
			flyTo(map, [coords.lon, coords.lat], { zoom: coords.z ?? 5, animate })
			firstRender.current = false
			return
		}

		if (selectedCoords) {
			if (map.getBounds()?.contains(selectedCoords)) {
				updateHash(map, { lat: map.getCenter().lat, lon: map.getCenter().lng, z: map.getZoom() })
			} else {
				flyTo(map, selectedCoords, { animate, padding: { bottom: 150 } })
			}
			firstRender.current = false
			setIsFollowing(false)
			return
		}

		if (!fetchingLocations) {
			if (!locations?.length) return
			const bounds = locations.reduce((acc, cur) => acc.extend(new LngLat(cur.lon, cur.lat)), new LngLatBounds())
			fitBounds(map, bounds, { padding: getPadding(), animate, maxZoom: 10 })
			firstRender.current = false
		}
	}, [map, location, locations, selectedCoords, fetchingLocations, setIsFollowing, bufferRadius.radius])

	return null
}

const getPadding = (isLocationSelected: boolean = false) => ({
	top: Math.min(200, globalThis.innerHeight * 0.1),
	// On desktop when location is selected, panel is open and timeline can be visible.
	// On mobile when location is selected, configured alerts for the asset are shown.
	bottom: isLocationSelected ? globalThis.innerHeight * 0.5 : Math.min(200, globalThis.innerHeight * 0.1),
	left: Math.min(200, globalThis.innerWidth * 0.1),
	right: Math.min(200, globalThis.innerWidth * 0.1),
})
